import { Injectable, Injector, NgZone, inject } from '@angular/core';
import { finalize, Observable, of, tap, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AuthResponse, LoginApiResponse, LoginResult, TLoginResponse } from './auth-response';
import { AuthData } from '../../models/auth-data';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { EnvironmentConfig } from '../../environment-config';
import { ErrorReporter } from '../error-reporter';
import { User, UserRaw } from '../../models/user';
import { AuthStateActions } from '../../state/auth.actions';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '../../state';
import { RbacService } from '../../../shared/services/access-control.service';
import { sha256 } from '@noble/hashes/sha256';
import { isOtpEnrollment, isOtpRequired, isSuccess } from './auth.util';

/**
 * Authentication service.
 */
@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private router = inject(Router);
    private readonly ngZone = inject(NgZone);
    private readonly http = inject(HttpClient);
    private readonly env = inject(EnvironmentConfig);
    private readonly errorReporter = inject(ErrorReporter);
    private readonly injector = inject(Injector);
    private readonly store$ = inject<Store<AppState>>(Store);
    private readonly rbacService = inject(RbacService);

    protected currentToken: string = null;
    private userEmail: string = null;
    private userDisplayName: string | null = null;

    /**
     * Initialize authentication.
     */
    async init(): Promise<AuthData> {
        return this.getUserDetails()
            .pipe(
                switchMap(result => {
                    this.rbacService.setAuthenticatedUser(result.user);
                    return of({
                        user: AuthResponse.toUser(result),
                    });
                })
            )
            .toPromise();
    }

    /**
     * First step: username+password.
     * Returns a LoginResult telling the UI what to do next.
     */
    login(email: string, password: string): Observable<LoginResult> {
        const body = { email, password };
        return this.http.post<LoginApiResponse>(`${this.env.backend}/auth/login`, body).pipe(
            // handle all 3 success HTTP-200 branches
            mergeMap(res => {
                if (isSuccess(res)) {
                    // 1) SUCCESS: we have tokens
                    this.setAccessToken(res.token);
                    return this.getUserDetails().pipe(
                        tap(value => {
                            this.store$.dispatch(AuthStateActions.loginCompleted({ user: value.user }));
                            this.rbacService.setAuthenticatedUser(value.user);
                            this.router.navigate(['/dashboard']);
                        }),
                        map(value => ({ kind: 'success', user: value.user }) as const)
                    );
                }

                if (isOtpRequired(res)) {
                    // 2) OTP REQUIRED: show OTP form in UI
                    return of({ kind: 'otp_required', message: res.message } as const);
                }

                if (isOtpEnrollment(res)) {
                    // 3) ENROLLMENT: redirect to Keycloak and stop stream
                    // window.location.assign(res.setup_url);
                    return of({ kind: 'redirected_for_enrollment', setup_url: res.setup_url } as const);
                }

                // 4) Unexpected shape
                return throwError(() => new Error('Unexpected login response'));
            }),
            catchError(error => {
                this.errorReporter.reportError(error);
                return of({ kind: 'error', error } as const);
            })
        );
    }

    /**
     * Second step: same creds + 6-digit OTP.
     * Call this after the UI prompts for the code.
     */
    loginWithOtp(email: string, password: string, otp: string): Observable<LoginResult> {
        const body = { email, password, otp };
        return this.http.post<LoginApiResponse>(`${this.env.backend}/auth/login`, body).pipe(
            mergeMap(res => {
                if (isSuccess(res)) {
                    this.setAccessToken(res.token);
                    return this.getUserDetails().pipe(
                        tap(value => {
                            this.store$.dispatch(AuthStateActions.loginCompleted({ user: value.user }));
                            this.rbacService.setAuthenticatedUser(value.user);
                            this.router.navigate(['/dashboard']);
                        }),
                        map(value => ({ kind: 'success', user: value.user }) as const)
                    );
                }
                if (isOtpRequired(res)) {
                    // wrong or missing OTP → keep prompting
                    return of({
                        kind: 'otp_required',
                        message: res.message ?? 'Invalid or expired OTP. Try again.',
                    } as const);
                }

                if (isOtpEnrollment(res)) {
                    // edge case: user wasn’t enrolled yet
                    // window.location.assign(res.setup_url);
                    return of({ kind: 'redirected_for_enrollment', setup_url: res.setup_url } as const);
                }

                return throwError(() => new Error('Unexpected login response'));
            }),
            catchError(error => {
                this.errorReporter.reportError(error);
                return of({ kind: 'error', error } as const);
            })
        );
    }

    /**
     * Perform login call to backend.
     */
    private getUserDetails(): Observable<AuthResponse> {
        return this.http.get<UserRaw>(`${this.env.backend}/auth/profile`).pipe(
            switchMap(user => {
                const authResponse = new AuthResponse();
                authResponse.user = User.fromRaw(user);
                this.userEmail = authResponse.user.email;
                this.userDisplayName = authResponse.user.displayName;
                return of(authResponse);
            })
        );
    }

    public getUserDisplayName(): string {
        return this.userDisplayName;
    }

    public getHashedUserEmail() {
        const encoder = new TextEncoder();
        const data = encoder.encode(this.userEmail);
        const hashBytes = sha256(data);
        const hashHex = Array.from(hashBytes)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
        return hashHex;
    }
    getAccessToken(): string {
        return this.currentToken;
    }

    setAccessToken(token: string) {
        this.currentToken = token;
    }

    /**
     * Perform logout call to backend.
     */
    private logoutFromBackend(): Observable<Response> {
        return this.http.post<Response>(`${this.env.backend}/auth/logout`, {});
    }

    redirectToLoginPage() {
        this.store$.dispatch(AuthStateActions.logoutCompleted());
        this.router.navigate(['/signin']);
    }

    refreshAccessToken(): Observable<TLoginResponse> {
        return this.http.post<TLoginResponse>(`${this.env.backend}/auth/refresh-token`, {});
    }
    /**
     * Executes a forced sign-out when the session token handling fails (e.g., refresh failed).
     * This bypasses the backend logout call, which would fail anyway.
     */
    forceSignOut(): void {
        this.setAccessToken(null); // Clear the in-memory token/subject
        this.redirectToLoginPage();
    }
    // Better user-initiated logout approach:
    logout(): Observable<Response> {
        // Backend endpoint require user to be logged, so first we hit backend and then no matter what we got from
        // backend, we sign out user from frontend.
        return this.logoutFromBackend().pipe(
            finalize(() => {
                this.setAccessToken(null);
                this.redirectToLoginPage();
            }),
            // Re-throw the error if the backend call failed
            catchError(err => throwError(() => err))
        );
    }

    forgetPassword(mail: string): Observable<Response> {
        return this.http.post<Response>(`${this.env.backend}/auth/forget-password`, { mail });
    }
}
